<template>
  <div
    v-if="!loading"
    class="editorial-container"
  >
    <div
      v-for="(section, sectionIndex) in sections"
      :key="`editorial-section-${sectionIndex}`"
      class="editorial"
      :class="getSectionColorClass(sectionIndex)"
    >
      <div class="content">
        <div class="title">{{ section.title }}</div>

        <UiSlider
          :slides-per-view="slidesPerView"
          :space-between="spaceBetween"
          :slides="section.articles"
          :options="{ slidesOffsetAfter: sliderOffset, slidesOffsetBefore: sliderOffset }"
        >
          <template #slide="{ slide, index }">
            <JournalCardSlide
              :index="index"
              :el="slide as Article"
              :data-index="index"
              :use-block="useBlock"
              :router-to="(slide as Article).url"
              class="slide"
            />
          </template>
          <template #rightArrowIcon>
            <IconBase
              name="right-arrow"
              color="--alias-color-icon-subtle"
            />
          </template>
          <template #leftArrowIcon>
            <IconBase
              class="mirror"
              name="right-arrow"
              color="--alias-color-icon-subtle"
            />
          </template>
        </UiSlider>
      </div>
    </div>
  </div>

  <div
    v-else
    class="editorial-container"
  >
    <div class="editorial editorial--orange">
      <div class="skeleton content">
        <div class="title">
          <SkeletonItem
            :style="{
              width: '250px',
              height: isDesktop ? '48px' : '28px',
              borderRadius: '8px',
            }"
            :animated="true"
          />
        </div>
        <UiSlider
          :slides-per-view="slidesPerView"
          :space-between="spaceBetween"
          :slides="[1, 2, 3]"
          :options="{ slidesOffsetAfter: sliderOffset, slidesOffsetBefore: sliderOffset }"
        >
          <template #slide>
            <JournalCardSkeleton :image-height="editorialSlideCardSkeletonImageHeight" />
          </template>
        </UiSlider>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { UiSlider } from '@experience/core-slider-ui';
  import { SkeletonItem } from '@experience/core-skeleton-lib';
  import { storeToRefs } from 'pinia';
  import { computed, onBeforeMount, onUpdated, nextTick } from 'vue';

  import { trackEvent } from '@/services/analytics';
  import { useEditorialArticlesEntity } from '@/pages/journal/entities/editorialArticles';
  import JournalCardSkeleton from '@/pages/journal/shared/ui/JournalCardSkeleton.vue';
  import JournalCardSlide from '@/pages/journal/shared/ui/JournalCardSlide.vue';
  import type { Article } from '@/shared/models/journal';
  import { IconBase } from '@/shared/ui/iconBase';
  import { observer } from '@/shared/utils/analytics';
  import { useScreenSize } from '@/shared/utils/breakpoints';

  const store = useEditorialArticlesEntity();
  const { getEditorialArticles } = store;
  const { isMobile, isTablet, isDesktop } = useScreenSize();

  const editorialSlideCardSkeletonImageHeight = computed(() => {
    if (isTablet.value) {
      return '211px';
    }

    return '280px';
  });

  const { sections, loading } = storeToRefs(store);

  const getSectionColorClass = (index: number) => {
    const colorPattern = index % 3;

    if (colorPattern === 0) {
      return 'editorial--orange';
    }

    if (colorPattern === 1) {
      return 'editorial--transparent';
    }

    return 'editorial--blue';
  };

  onBeforeMount(() => {
    getEditorialArticles();
  });

  onUpdated(async () => {
    await soguInitObservers();
  });

  const useBlock = (i: number, id: number) => {
    trackEvent('journal:article.click', {
      block: 'special',
      position: i,
      article_id: id,
    });
  };

  const soguInitObservers = async () => {
    await nextTick(() => {
      const nodeList = document.getElementsByClassName('slide');

      Array.prototype.forEach.call(nodeList, (el: Element) => {
        if (el instanceof HTMLElement) {
          const i: number = Number(el?.dataset?.index);

          if (i <= nodeList.length) {
            observer(el, async () => {
              trackEvent('journal:article.view', {
                block: 'special',
                position: i,
                article_id: Number(el?.id),
              });
            });
          }
        }
      });
    });
  };

  const slidesPerView = computed(() => {
    if (isTablet.value && !isMobile.value) {
      return 2.45;
    }

    if (isMobile.value) {
      return 1.2;
    }

    return 3;
  });

  const spaceBetween = computed(() => {
    if (isTablet.value || isMobile.value) {
      return 16;
    }

    return 24;
  });

  const sliderOffset = computed(() => {
    if (isTablet.value && !isMobile.value) {
      return 20;
    }

    if (isMobile.value) {
      return 24;
    }

    return 0;
  });
</script>

<style lang="scss" scoped>
  .editorial-container {
    margin: 80px auto 0;

    @include tablet {
      margin: 40px auto 0;
    }
  }

  .editorial {
    overflow: hidden;
    max-width: 1360px;
    margin: 8px auto 0;
    border-radius: 8px;

    &:nth-child(1) {
      margin: 0 auto;
    }

    &--orange {
      background-color: var(--alias-color-background-accent-orange-lightest);
    }

    &--transparent {
      background-color: transparent;
    }

    &--blue {
      background-color: var(--alias-color-background-accent-blue-lightest);
    }

    @include tablet {
      border-radius: 0;
    }
  }

  .content {
    padding: 56px;
    color: var(--alias-color-text-default);

    @include tablet {
      padding: 40px 0;
    }

    @include mobile {
      padding: 24px 0;
    }
  }

  .block-name,
  .title {
    @include tablet {
      margin-left: 20px;
    }
  }

  .block-name {
    @include base-400-400-regular;

    @include tablet {
      @include base-300-300-regular;
    }
  }

  .title {
    margin-bottom: 24px;

    @include base-900-850-extra;

    @include tablet {
      font-size: 24px;

      @include base-700-500-extra;
    }

    @include mobile {
      margin-right: 40px;
      margin-bottom: 16px;
    }
  }

  :deep(.btn-prev) {
    top: 25%;
    transform: translateX(-50%);
  }

  :deep(.btn-next) {
    top: 25%;
    transform: translateX(50%);
  }

  .mirror {
    transform: scaleX(-1);
  }
</style>
